import { useMemo } from "react";

import {
  convertCountryNameToCountryCode,
  CountryCode,
  CountryNames,
  isVariantActive,
  resolveRegionCode,
  TicketingFormCategory,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { trpc } from "@/common/helpers/trpc";

import { MEMBER_FOR_SOMEONE_ELSE } from "./membershipRebuyLink";
import { MEMBERSHIP_REBUY_PREFILL_PARAM, readMembershipRebuyPrefill } from "./membershipRebuyPrefill";

/**
 * Extracts form-prefill data from URL search params (email personalization / renewal magic links).
 * `contactInfo` is the recipient's contact fields, applied client-side only so Cloudflare never caches
 * user information in SSR HTML. A link carrying `emailId` comes from a Zeffy email: its contact fields come
 * from the server (`resolveEmailPrefill`, answered once), never from the URL, so a forwarded link stays clean.
 * `rateId` preselects a tier; it is only returned for form categories that use the simple "select one rate, set quantity" model — auctions (bids) and shop/variant forms are
 * excluded so their selection state isn't corrupted. Pass `formCategory` to opt into `rateId`.
 * `memberFor=other` is set by the membership rebuy link, where the buyer already keeps a membership.
 */
export const usePrefillSearchParams = (formCategory?: TicketingFormCategory) => {
  const searchParams = useSearchParams();
  const emailId = searchParams.get("emailId");
  const { data: emailPrefill, isLoading: isPrefillPending } = trpc.resolveEmailPrefill.useQuery(
    { emailId: emailId ?? "" },
    {
      enabled: Boolean(emailId),
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }
  );

  return useMemo(() => {
    const contactParams = emailId ? toSearchParams(emailPrefill) : searchParams;
    const buyerDetails =
      searchParams.get("memberFor") === MEMBER_FOR_SOMEONE_ELSE
        ? readMembershipRebuyPrefill(searchParams.get(MEMBERSHIP_REBUY_PREFILL_PARAM), searchParams.get("rateId"))
        : undefined;
    const state = contactParams.get("state");
    const region = contactParams.get("region");
    const country = buyerDetails?.country ?? (contactParams.get("country") || undefined);
    const rawRegion = buyerDetails?.region ?? (state || region || undefined);
    const countryCode = country
      ? (convertCountryNameToCountryCode(country as CountryNames) ?? (country as CountryCode))
      : undefined;

    const contactInfo = {
      firstName: contactParams.get("firstName") || undefined,
      lastName: contactParams.get("lastName") || undefined,
      email: contactParams.get("email") || undefined,
      address: buyerDetails?.address ?? (contactParams.get("address") || undefined),
      city: buyerDetails?.city ?? (contactParams.get("city") || undefined),
      postalCode: buyerDetails?.postalCode ?? (contactParams.get("postalCode") || undefined),
      region: country ? (resolveRegionCode(countryCode, rawRegion) ?? rawRegion) : undefined,
      country,
    };

    const supportsRatePreselection =
      formCategory !== undefined && formCategory !== TicketingFormCategory.Auction && !isVariantActive(formCategory);
    const rateId = supportsRatePreselection ? searchParams.get("rateId") || undefined : undefined;

    return {
      contactInfo,
      rateId,
      buyerDetails,
      isMemberSomeoneElse: searchParams.get("memberFor") === MEMBER_FOR_SOMEONE_ELSE,
      isPrefillPending,
    };
  }, [searchParams, formCategory, emailId, emailPrefill, isPrefillPending]);
};

const toSearchParams = (prefill: Record<string, string | null> | null | undefined) =>
  new URLSearchParams(Object.entries(prefill ?? {}).filter((entry): entry is [string, string] => Boolean(entry[1])));
